import { useLocation } from "react-router-dom";

interface RouteTitle {
  path: string;
  title: string;
  exact?: boolean;
}

const routeTitles: RouteTitle[] = [
  { path: "/", title: "Dashboard", exact: true },
  { path: "/cases", title: "Case Management", exact: true },
  { path: "/needs-assessment", title: "Needs Assessment", exact: true },
  { path: "/cases/", title: "Case Details" }, // For case detail pages like /cases/123
  { path: "/debug", title: "API Debug", exact: true },

  // Healthcare Compliance routes
  { path: "/healthcare/upload", title: "Document Upload", exact: true },
  { path: "/healthcare/files", title: "Document Library", exact: true },
  { path: "/healthcare/processing", title: "Processing Queue", exact: true },
  { path: "/healthcare/rules", title: "Compliance Rules", exact: true },
  { path: "/healthcare/extraction/", title: "Extracted Data" }, // For extraction detail pages

  // Subscription routes
  { path: "/subscriptions/plans", title: "Subscription Plans", exact: true },
  {
    path: "/subscriptions/create",
    title: "Create Package",
    exact: true,
  },
  { path: "/subscriptions/purchase/", title: "Purchase Subscription" }, // For purchase flow pages
  {
    path: "/subscriptions/payment/success",
    title: "Payment Successful",
    exact: true,
  },
  {
    path: "/subscriptions/payment/cancel",
    title: "Payment Cancelled",
    exact: true,
  },
  { path: "/subscriptions/manage", title: "Manage Subscriptions", exact: true },

  // Audit logs
  { path: "/audit-logs", title: "Audit Logs", exact: true },

  // Support ticketing
  { path: "/support-tickets/triage", title: "Support Triage", exact: true },
  {
    path: "/support-tickets/analytics",
    title: "Support Analytics",
    exact: true,
  },
  { path: "/support-tickets/", title: "Ticket Details" },
  { path: "/support-tickets", title: "Support Tickets", exact: true },

  // Cost explorer
  { path: "/cost-explorer", title: "API Cost Explorer", exact: true },

  // Admin routes
  { path: "/admin/document-types", title: "Document Types", exact: true },
  { path: "/admin/prompts", title: "AI Prompts", exact: true },
  {
    path: "/admin/subscriptions/approvals",
    title: "Payment Approvals",
    exact: true,
  },
];

/**
 * Hook to get the current page title based on the route
 */
export const usePageTitle = (): string => {
  const location = useLocation();
  const currentPath = location.pathname;

  // First, try to find an exact match
  const exactMatch = routeTitles.find(
    (route) => route.exact && route.path === currentPath,
  );

  if (exactMatch) {
    return exactMatch.title;
  }

  // Check for case detail pages specifically
  if (currentPath.match(/^\/cases\/[^/]+$/)) {
    return "Case Details";
  }

  // Check for healthcare extraction pages specifically
  if (currentPath.match(/^\/healthcare\/extraction\/[^/]+$/)) {
    return "Extracted Data";
  }

  // Check for subscription purchase pages specifically
  if (currentPath.match(/^\/subscriptions\/purchase\/[^/]+$/)) {
    return "Purchase Subscription";
  }

  // Then, try to find a partial match (for other dynamic routes)
  const partialMatch = routeTitles.find(
    (route) => !route.exact && currentPath.startsWith(route.path),
  );

  if (partialMatch) {
    return partialMatch.title;
  }

  // Default fallback
  return "Alpha Health";
};

export default usePageTitle;
